import { authenticatedInstance } from '../index/index.service';

// Summary: Keeps a short-lived, server-authoritative table-view heartbeat for the current POS device.
// Details: A live table view blocks disruptive long-press mutations from other devices.
class TablePresenceService {
  private clientId(tenantId: string): string {
    const key = `posTablePresenceClient:${tenantId}`;
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const created = typeof crypto?.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(key, created);
    return created;
  }

  async touch(tenantId: string, tableId: number, employeeSession: string): Promise<{ claimed: boolean; employeeName?: string }> {
    const response = await authenticatedInstance.put<{ claimed?: boolean; employeeName?: string }>(
      `/pos/tenant/${encodeURIComponent(tenantId)}/table/${tableId}/presence`,
      {},
      { headers: { 'x-employee-session': employeeSession }, params: { clientId: this.clientId(tenantId) } },
    );
    return {
      claimed: response.data?.claimed === true,
      employeeName: response.data?.employeeName,
    };
  }

  async remove(tenantId: string, tableId: number, employeeSession: string): Promise<void> {
    await authenticatedInstance.delete(
      `/pos/tenant/${encodeURIComponent(tenantId)}/table/${tableId}/presence`,
      { headers: { 'x-employee-session': employeeSession }, params: { clientId: this.clientId(tenantId) } },
    );
  }

  removeOnPageExit(tenantId: string, tableId: number, employeeSession: string): void {
    const url = authenticatedInstance.getUri({
      url: `/pos/tenant/${encodeURIComponent(tenantId)}/table/${tableId}/presence`,
      params: { clientId: this.clientId(tenantId) },
    });
    void fetch(url, {
      method: 'DELETE',
      headers: { 'x-employee-session': employeeSession },
      credentials: 'include',
      keepalive: true,
    }).catch(() => undefined);
  }

  async check(tenantId: string, tableId: number, employeeSession: string): Promise<{ occupied: boolean; employeeName?: string }> {
    const response = await authenticatedInstance.post<{ occupied?: boolean; employeeName?: string }>(
      `/pos/tenant/${encodeURIComponent(tenantId)}/table/${tableId}/presence/check`,
      {},
      { headers: { 'x-employee-session': employeeSession } },
    );
    return {
      occupied: response.data?.occupied === true,
      employeeName: response.data?.employeeName,
    };
  }
}

export const tablePresenceService = new TablePresenceService();
